import { ApiError } from '@discovr/common';
import { campaignCopy } from './campaignCopy';

/**
 * Common error-message helper. Builds on `humanizeApiError` from
 * `@discovr/common`, but provides a single place to layer
 * domain-specific overrides if/when needed.
 *
 * Pattern: `errorMessage(err, fallback)` returns the first non-empty
 * string in this priority:
 *   1. Readable copy for known ApiError codes (CODE_MESSAGES)
 *   2. The thrown Error's `.message` (when meaningful)
 *   3. The caller's `fallback` string
 *   4. A generic "Something went wrong" sentinel
 */

// Codes whose server text is not fit for a toast; shared by every write mutation.
const CODE_MESSAGES: Readonly<Record<string, string>> = {
  POST_ON_HOLD: campaignCopy.creator.hold.heldActionReason,
  STAGE_CHANGED: campaignCopy.creator.rollback.stageChanged,
  NOT_ROLLBACKABLE: campaignCopy.creator.rollback.notRollbackable,
  STAGE_DISABLED: campaignCopy.creator.rollback.stageDisabled,
  PRODUCTS_LOCKED: campaignCopy.creator.previousProducts.locked,
  PROFILE_UNOFFERABLE: campaignCopy.creator.rollback.profileUnofferable,
  CONFIRM_MISMATCH: campaignCopy.typedConfirm.prompt,
  BAD_GATEWAY: "Couldn't reach the service. Try again.",
};

export function errorMessage(err: unknown, fallback?: string): string {
  const mapped =
    err instanceof ApiError && Object.hasOwn(CODE_MESSAGES, err.code) ? CODE_MESSAGES[err.code] : undefined;
  if (mapped) return mapped;
  if (err instanceof Error && err.message) return err.message;
  if (fallback) return fallback;
  return 'Something went wrong';
}
